import React, { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import * as RMenu from '@radix-ui/react-dropdown-menu';
import { Send, Paperclip, X, Trash2, ChevronDown, Loader2, FileText, Clock, MailCheck, Zap, Sparkles } from 'lucide-react';
import api from '../../utils/api';
import MailEditor from './MailEditor';
import { usePresence, PresenceNote } from './TeamPanel';
import { replySubject, forwardSubject, replyTo, quoteBlock, fmtSize, avatarColor, initials } from './mailUi';

// New mail / Reply / Reply all / Forward, in the reading pane (as Outlook does). The editor holds
// what you write; the quoted original is added underneath when it is sent, exactly as received.
// What you are writing is kept in this browser until it is sent or discarded.
const DRAFT_KEY = 'rostery-mail-draft';
const readDraft = () => { try { return JSON.parse(window.localStorage.getItem(DRAFT_KEY) || 'null'); } catch { return null; } };
const saveDraft = (d) => { try { window.localStorage.setItem(DRAFT_KEY, JSON.stringify(d)); } catch { /* storage blocked */ } };
const clearDraft = () => { try { window.localStorage.removeItem(DRAFT_KEY); } catch { /* storage blocked */ } };

export default function Compose({ accounts, start, onClose, onSent, prefs, aiOn }) {
  const P = prefs || {};
  const mode = start.mode || 'new';
  const src = start.source || null;
  // Replying in a shared mailbox: the team sees "writing a reply", and this shows if someone else is.
  const srcAccount = src && (accounts || []).find((a) => a.id === src.account_id);
  const replyOthers = usePresence(src && src.id, 'replying', !!(src && mode !== 'new' && srcAccount && srcAccount.visibility !== 'owner'));
  const has = (id) => id && accounts.some((a) => a.id === id);
  const prefAccount = mode === 'new' && P.compose && has(P.compose.defaultAccount) ? P.compose.defaultAccount : null;
  const defaultAccount = (has(start.account_id) && start.account_id) || prefAccount || (src && has(src.account_id) && src.account_id) || (accounts[0] && accounts[0].id) || '';
  const [accountId, setAccountId] = useState(defaultAccount);
  const account = accounts.find((a) => a.id === accountId) || accounts[0];
  const [snippets, setSnippets] = useState([]);
  const touched = useRef(false);
  const editorRef = useRef(null);
  // The signature this mailbox uses for new mail, or for replies and forwards (Settings › Signatures).
  const sigFor = (acc, list) => {
    const pick = ((P.signatures || {})[acc && acc.id] || {})[mode === 'new' ? 'newId' : 'replyId'];
    const s = pick && list.find((x) => x.id === pick && x.kind === 'signature');
    const htmlSig = pick === 'none' ? null : s ? s.html : acc && acc.signature_html;
    return htmlSig ? `<p></p><p></p>${htmlSig}` : '';
  };
  const sig = sigFor(account, snippets);
  const initial = useMemo(() => {
    // Back from Undo: exactly what was being sent.
    if (start.restore) return { to: start.restore.to || [], cc: start.restore.cc || [], bcc: start.restore.bcc || [], subject: start.restore.subject || '', html: start.restore.html || '' };
    const saved = readDraft();
    if (mode === 'new' && saved && saved.mode === 'new' && !start.to) return saved;
    if (mode === 'new') return { to: start.to || [], cc: [], bcc: [], subject: start.subject || '', html: (start.body || '') + sig };
    const r = replyTo(src, account && account.email, mode === 'replyAll');
    return {
      to: mode === 'forward' ? [] : r.to, cc: mode === 'forward' ? [] : r.cc, bcc: [],
      subject: mode === 'forward' ? forwardSubject(src.subject) : replySubject(src.subject), html: (start.body || '') + sig,
    };
    // Built once per opening.
    // eslint-disable-next-line
  }, []);
  const [to, setTo] = useState(initial.to);
  const [cc, setCc] = useState(initial.cc);
  const [bcc, setBcc] = useState(initial.bcc);
  const [showCc, setShowCc] = useState(initial.cc.length > 0 || initial.bcc.length > 0 || !!(P.compose && P.compose.alwaysCcBcc));
  const [subject, setSubject] = useState(initial.subject);
  const [html, setHtml] = useState(initial.html);
  const [files, setFiles] = useState((start.restore && start.restore.files) || []);
  const [sending, setSending] = useState(false);
  const [receipt, setReceipt] = useState(!!(P.send && P.send.readReceipt));
  const [improving, setImproving] = useState(false);
  // Signatures, templates and quick texts; the signature goes in once they arrive (if nothing is typed yet).
  useEffect(() => {
    api.get('/mail/snippets').then((r) => {
      const list = r.data || [];
      setSnippets(list);
      if (!touched.current && !start.restore) setHtml((start.body || '') + sigFor(account, list));
    }).catch(() => {});
  }, []); // once per opening
  const changeAccount = (id) => {
    setAccountId(id);
    const next = accounts.find((a) => a.id === id);
    if (!touched.current) setHtml((start.body || '') + sigFor(next, snippets));
  };
  const useTemplate = (t) => {
    if (t.subject && (!subject.trim() || window.confirm(`Replace the subject with "${t.subject}"?`))) setSubject(t.subject);
    setHtml(t.html + sig);
    touched.current = true;
  };
  const insertQuick = (t) => {
    const ed = editorRef.current;
    if (ed) ed.chain().focus().insertContent(t.html).run(); else setHtml((h) => h + t.html);
    touched.current = true;
  };
  const improve = async (how) => {
    setImproving(true);
    try {
      const r = await api.post('/mail/ai', { action: 'improve', text: html, mode: how });
      const out = String(r.data.text || '').split(/\n{2,}/).map((p) => `<p>${p.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/\n/g, '<br>')}</p>`).join('');
      if (out) { setHtml(out + sig); touched.current = true; toast.success('Rewritten. Check it before sending.'); }
    } catch (e) { toast.error(e.response?.data?.message || 'The AI could not help just now.'); } finally { setImproving(false); }
  };
  const [pickTime, setPickTime] = useState(false);
  const [when, setWhen] = useState('');
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quote, setQuote] = useState(null);
  const fileRef = useRef(null);

  // The original, with its images, for the quote.
  useEffect(() => {
    if (!src || mode === 'new') return;
    // "Include the original message" (Settings › Replies and forwards); a forward always carries it.
    if (mode !== 'forward' && P.replies && P.replies.includeOriginal === false) return;
    api.get(`/mail/messages/${src.id}`, { params: { images: 1, peek: 1 } }).then((r) => setQuote(quoteBlock({ ...src, ...r.data }))).catch(() => setQuote(quoteBlock(src)));
  }, [src, mode]);
  useEffect(() => { if (mode === 'new') saveDraft({ mode, to, cc, bcc, subject, html }); }, [mode, to, cc, bcc, subject, html]);

  const send = async (sendAt = null) => {
    if (!to.length && !cc.length && !bcc.length) { toast.error('Add at least one recipient.'); return; }
    if (!subject.trim() && !window.confirm('Send this message without a subject?')) return;
    setSending(true);
    try {
      const fd = new FormData();
      const list = (x) => x.map((a) => (a.name ? `"${a.name.replace(/"/g, "'")}" <${a.address}>` : a.address)).join(', ');
      fd.append('account_id', accountId); fd.append('mode', mode); if (src) fd.append('source_id', src.id);
      fd.append('to', list(to)); fd.append('cc', list(cc)); fd.append('bcc', list(bcc)); fd.append('subject', subject);
      const font = (P.compose && P.compose.font) || 'Segoe UI'; const size = (P.compose && P.compose.fontSize) || 14;
      fd.append('html', `<div style="font-family:'${font}',Arial,sans-serif;font-size:${size}px;color:#201f1e">${html}</div>${quote || ''}`);
      files.forEach((f) => fd.append('attachments', f));
      if (sendAt instanceof Date && !Number.isNaN(sendAt.getTime())) fd.append('send_at', sendAt.toISOString());
      if (receipt) fd.append('read_receipt', '1');
      const r = await api.post('/mail/send', fd);
      if (mode === 'new') clearDraft();
      if (!r.data.queued) toast.success('Sent');
      // With undo send (or send later) the message waits a moment; Undo brings this back to edit.
      onSent && onSent({ ...r.data, restore: { mode, source: src, account_id: accountId, to, cc, bcc, subject, html, files } });
      onClose();
    } catch (e) { toast.error(e.response?.data?.message || (e.response ? 'Could not send. Please try again.' : `Could not send: ${(e && e.message) || 'no connection'}.`)); } finally { setSending(false); }
  };
  const discard = () => { if (mode === 'new') clearDraft(); onClose(); };
  useEffect(() => {
    const k = (e) => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); send(); } };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  });
  const total = files.reduce((t, f) => t + f.size, 0);
  const addFiles = (list) => {
    const next = [...files, ...Array.from(list || [])].slice(0, 15);
    if (next.reduce((t, f) => t + f.size, 0) > 25 * 1024 * 1024) { toast.error('Attachments can add up to 25 MB.'); return; }
    setFiles(next);
  };

  return (
    <div className="flex flex-col h-full min-h-0 bg-white" onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}>
      {(replyOthers || []).some((p) => p.state === 'replying') && <div className="px-4 pt-2.5"><PresenceNote others={replyOthers.filter((p) => p.state === 'replying')} replying /></div>}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-200">
        <button type="button" onClick={() => send()} disabled={sending}
          className="inline-flex items-center gap-2 h-9 px-4 rounded-md bg-[#0f6cbd] text-white text-[13.5px] font-semibold hover:bg-[#115ea3] disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[#0f6cbd]">
          {sending ? <Loader2 size={15} className="animate-spin" aria-hidden /> : <Send size={15} aria-hidden />} Send
        </button>
        <SendLater onPick={(d) => send(d)} onCustom={() => setPickTime(true)} disabled={sending} />
        <button type="button" onClick={() => setReceipt((v) => !v)} aria-pressed={receipt} title="Ask for a read receipt"
          className={clsx('hidden sm:inline-flex items-center gap-1.5 h-9 px-2.5 rounded-md text-[12.5px]', receipt ? 'bg-sky-50 text-[#0f6cbd] font-semibold' : 'text-slate-600 hover:bg-slate-100')}>
          <MailCheck size={15} aria-hidden /> Receipt
        </button>
        <button type="button" onClick={() => fileRef.current && fileRef.current.click()} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-[13px] text-slate-700 hover:bg-slate-100">
          <Paperclip size={15} aria-hidden /> Attach
        </button>
        <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
        <SnippetMenu label="Templates" icon={FileText} items={snippets.filter((s) => s.kind === 'template')} onPick={useTemplate} empty="Make templates in Settings › Templates." />
        <SnippetMenu label="Quick text" icon={Zap} items={snippets.filter((s) => s.kind === 'quicktext')} onPick={insertQuick} empty="Make quick texts in Settings › Quick texts." />
        {aiOn && <ImproveMenu busy={improving} onPick={improve} />}
        <span className="flex-1" />
        <span className="hidden sm:inline text-[12px] text-slate-500">Ctrl+Enter to send</span>
        <button type="button" onClick={discard} title="Discard" aria-label="Discard" className="w-9 h-9 rounded-md flex items-center justify-center text-slate-500 hover:bg-slate-100 hover:text-rose-700"><Trash2 size={16} aria-hidden /></button>
      </div>
      {pickTime && (
        <div className="flex flex-wrap items-center gap-2 px-4 py-2 bg-sky-50/70 border-b border-sky-100 text-[13px]">
          <Clock size={15} className="text-[#0f6cbd]" aria-hidden />
          <label htmlFor="send-when" className="text-slate-700">Send on</label>
          <input id="send-when" type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} min={localNow()}
            className="h-8 px-2 rounded-md ring-1 ring-slate-300 bg-white text-[13px] focus:outline-none focus:ring-2 focus:ring-[#0f6cbd]" />
          <button type="button" disabled={!when} onClick={() => send(new Date(when))} className="h-8 px-3 rounded-md bg-[#0f6cbd] text-white font-semibold disabled:opacity-50">Schedule</button>
          <button type="button" onClick={() => setPickTime(false)} className="h-8 px-2 rounded-md text-slate-600 hover:bg-white">Cancel</button>
        </div>
      )}
      <div className="flex-1 min-h-0 overflow-y-auto mail-scroll">
        <div className="px-4 divide-y divide-slate-100 border-b border-slate-200">
          <Row label="From">
            <div className="relative inline-flex items-center">
              <select value={accountId} onChange={(e) => changeAccount(e.target.value)} aria-label="Send from"
                className="appearance-none bg-transparent pr-6 py-2 text-[13.5px] text-slate-800 focus:outline-none cursor-pointer">
                {accounts.map((a) => <option key={a.id} value={a.id}>{a.display_name ? `${a.display_name} <${a.email}>` : a.email}</option>)}
              </select>
              <ChevronDown size={14} className="absolute right-0 text-slate-400 pointer-events-none" aria-hidden />
            </div>
          </Row>
          <Row label="To" extra={!showCc && <button type="button" onClick={() => setShowCc(true)} className="text-[12.5px] text-[#0f6cbd] hover:underline">Cc &amp; Bcc</button>}>
            <Recipients value={to} onChange={setTo} autoFocus={mode === 'new' || mode === 'forward'} label="To" />
          </Row>
          {showCc && <Row label="Cc"><Recipients value={cc} onChange={setCc} label="Cc" /></Row>}
          {showCc && <Row label="Bcc"><Recipients value={bcc} onChange={setBcc} label="Bcc" /></Row>}
          <Row label="Subject">
            <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Add a subject" aria-label="Subject"
              className="w-full py-2 text-[14px] font-semibold text-slate-900 placeholder:font-normal placeholder:text-slate-400 bg-transparent focus:outline-none" />
          </Row>
        </div>
        {files.length > 0 && (
          <div className="flex flex-wrap gap-2 px-4 py-2.5 border-b border-slate-100">
            {files.map((f, i) => (
              <span key={`${f.name}-${i}`} className="inline-flex items-center gap-2 rounded-md ring-1 ring-slate-200 bg-slate-50 pl-2 pr-1 py-1 text-[12.5px]">
                <FileText size={14} className="text-slate-500" aria-hidden /><span className="max-w-[180px] truncate">{f.name}</span><span className="text-slate-400">{fmtSize(f.size)}</span>
                <button type="button" aria-label={`Remove ${f.name}`} onClick={() => setFiles(files.filter((_, j) => j !== i))} className="w-6 h-6 rounded flex items-center justify-center text-slate-400 hover:bg-slate-200 hover:text-slate-700"><X size={13} /></button>
              </span>
            ))}
            <span className="self-center text-[12px] text-slate-500">{fmtSize(total)} of 25 MB</span>
          </div>
        )}
        <MailEditor value={html} onChange={(v) => { touched.current = true; setHtml(v); }} autoFocus={mode === 'reply' || mode === 'replyAll'} minHeight={240}
          editorRef={editorRef} font={P.compose && P.compose.font} fontSize={P.compose && P.compose.fontSize} />
        {quote && (
          <div className="px-4 pb-4">
            <button type="button" onClick={() => setQuoteOpen((v) => !v)} className="text-[12.5px] font-semibold text-slate-500 hover:text-slate-800 rounded px-2 py-1 bg-slate-100">
              {quoteOpen ? 'Hide' : 'Show'} the original message{mode === 'forward' && src && src.attachments && src.attachments.length ? ` and its ${src.attachments.length} attachment${src.attachments.length === 1 ? '' : 's'}` : ''}
            </button>
            {quoteOpen && <iframe title="Original message" sandbox="" srcDoc={`<base target="_blank"><div style="font-family:Segoe UI,Arial,sans-serif;font-size:13px">${quote}</div>`} className="w-full mt-2 h-80 rounded-md ring-1 ring-slate-200" />}
          </div>
        )}
      </div>
    </div>
  );
}

// Templates and quick texts, from the compose bar.
function SnippetMenu({ label, icon: Icon, items, onPick, empty }) {
  return (
    <RMenu.Root>
      <RMenu.Trigger asChild>
        <button type="button" aria-label={label} title={label} className="inline-flex items-center gap-1.5 h-9 px-2.5 rounded-md text-[12.5px] text-slate-600 hover:bg-slate-100 data-[state=open]:bg-slate-100">
          <Icon size={15} aria-hidden /> <span className="hidden sm:inline">{label}</span>
        </button>
      </RMenu.Trigger>
      <RMenu.Portal>
        <RMenu.Content align="start" sideOffset={4} className="z-[80] min-w-[240px] max-h-[340px] overflow-y-auto bg-white rounded-lg shadow-lg ring-1 ring-slate-200 p-1">
          {items.length ? items.map((s) => (
            <RMenu.Item key={s.id} onSelect={() => onPick(s)} className="px-2.5 py-2 rounded-md text-[13px] text-slate-700 cursor-pointer outline-none data-[highlighted]:bg-slate-100">
              <span className="block font-medium">{s.name}</span>
              <span className="block text-[11.5px] text-slate-500 truncate max-w-[260px]">{String(s.html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()}</span>
            </RMenu.Item>
          )) : <p className="px-2.5 py-2 text-[12.5px] text-slate-500">{empty}</p>}
        </RMenu.Content>
      </RMenu.Portal>
    </RMenu.Root>
  );
}
function ImproveMenu({ busy, onPick }) {
  return (
    <RMenu.Root>
      <RMenu.Trigger asChild>
        <button type="button" disabled={busy} className="inline-flex items-center gap-1.5 h-9 px-2.5 rounded-md text-[12.5px] text-violet-700 hover:bg-violet-50 data-[state=open]:bg-violet-50 disabled:opacity-60">
          {busy ? <Loader2 size={15} className="animate-spin" aria-hidden /> : <Sparkles size={15} aria-hidden />} <span className="hidden sm:inline">Improve</span>
        </button>
      </RMenu.Trigger>
      <RMenu.Portal>
        <RMenu.Content align="start" sideOffset={4} className="z-[80] min-w-[220px] bg-white rounded-lg shadow-lg ring-1 ring-slate-200 p-1">
          {[['professional', 'Make it clear and professional'], ['shorter', 'Make it shorter'], ['friendly', 'Make it friendlier'], ['grammar', 'Fix spelling and grammar']].map(([k, l]) => (
            <RMenu.Item key={k} onSelect={() => onPick(k)} className="px-2.5 py-2 rounded-md text-[13px] text-slate-700 cursor-pointer outline-none data-[highlighted]:bg-slate-100">{l}</RMenu.Item>
          ))}
        </RMenu.Content>
      </RMenu.Portal>
    </RMenu.Root>
  );
}

// Send later: the usual times, or one you choose.
const at = (days, hour, weekday = null) => {
  const d = new Date();
  if (weekday !== null) { d.setDate(d.getDate() + ((weekday + 7 - d.getDay()) % 7 || 7)); } else d.setDate(d.getDate() + days);
  d.setHours(hour, 0, 0, 0);
  return d;
};
const localNow = () => { const d = new Date(Date.now() - new Date().getTimezoneOffset() * 60000); return d.toISOString().slice(0, 16); };
function SendLater({ onPick, onCustom, disabled }) {
  const fmt = (d) => d.toLocaleString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
  const opts = [['Tomorrow morning', at(1, 8)], ['Tomorrow afternoon', at(1, 13)], ['Monday morning', at(0, 8, 1)]];
  return (
    <RMenu.Root>
      <RMenu.Trigger asChild>
        <button type="button" disabled={disabled} title="Send later" aria-label="Send later"
          className="-ml-1.5 h-9 w-8 rounded-md flex items-center justify-center bg-[#0f6cbd] text-white hover:bg-[#115ea3] disabled:opacity-60 border-l border-white/30">
          <ChevronDown size={15} aria-hidden />
        </button>
      </RMenu.Trigger>
      <RMenu.Portal>
        <RMenu.Content align="start" sideOffset={4} className="z-[80] min-w-[240px] bg-white rounded-lg shadow-lg ring-1 ring-slate-200 p-1">
          <RMenu.Label className="px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Send later</RMenu.Label>
          {opts.map(([label, d]) => (
            <RMenu.Item key={label} onSelect={() => onPick(d)} className="flex items-center justify-between gap-3 px-2.5 py-2 rounded-md text-[13px] text-slate-700 cursor-pointer outline-none data-[highlighted]:bg-slate-100">
              <span>{label}</span><span className="text-[12px] text-slate-500">{fmt(d)}</span>
            </RMenu.Item>
          ))}
          <RMenu.Separator className="my-1 h-px bg-slate-100" />
          <RMenu.Item onSelect={onCustom} className="flex items-center gap-2 px-2.5 py-2 rounded-md text-[13px] text-slate-700 cursor-pointer outline-none data-[highlighted]:bg-slate-100">
            <Clock size={14} className="text-slate-500" aria-hidden /> Choose a date and time
          </RMenu.Item>
        </RMenu.Content>
      </RMenu.Portal>
    </RMenu.Root>
  );
}

function Row({ label, children, extra }) {
  return (
    <div className="flex items-start gap-3">
      <span className="w-14 flex-shrink-0 pt-2.5 text-[13px] text-slate-500">{label}</span>
      <div className="flex-1 min-w-0">{children}</div>
      {extra && <div className="pt-2.5">{extra}</div>}
    </div>
  );
}

// Address chips with suggestions (people in your mail, and participants' contacts).
const EMAIL = /^[^\s@<>()",;:]+@[^\s@<>()",;:.]+(\.[^\s@<>()",;:.]+)+$/;
function Recipients({ value, onChange, autoFocus, label }) {
  const [text, setText] = useState('');
  const [sugg, setSugg] = useState([]);
  const [active, setActive] = useState(0);
  const seq = useRef(0);
  useEffect(() => {
    const t = text.trim();
    if (t.length < 2) { setSugg([]); return undefined; }
    const my = ++seq.current;
    const h = setTimeout(() => api.get('/mail/suggest', { params: { q: t } }).then((r) => { if (my === seq.current) { setSugg((r.data || []).filter((s) => !value.some((v) => v.address === s.address))); setActive(0); } }).catch(() => {}), 180);
    return () => clearTimeout(h);
  }, [text, value]);
  const add = (a) => { if (!value.some((v) => v.address === a.address)) onChange([...value, a]); setText(''); setSugg([]); };
  const commit = () => {
    const parts = text.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);
    const good = parts.filter((p) => EMAIL.test(p)).map((p) => ({ name: '', address: p.toLowerCase() }));
    if (good.length) { onChange([...value, ...good.filter((g) => !value.some((v) => v.address === g.address))]); setText(''); setSugg([]); return true; }
    return false;
  };
  return (
    <div className="relative">
      <div className="flex flex-wrap items-center gap-1 py-1.5">
        {value.map((a) => (
          <span key={a.address} className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 pl-1 pr-1 py-0.5 text-[13px] text-slate-800" title={a.address}>
            <span className="w-5 h-5 rounded-full text-white text-[9.5px] font-bold flex items-center justify-center" style={{ background: avatarColor(a.address) }} aria-hidden>{initials(a.name, a.address)}</span>
            {a.name || a.address}
            <button type="button" aria-label={`Remove ${a.address}`} onClick={() => onChange(value.filter((v) => v.address !== a.address))} className="w-5 h-5 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-200 hover:text-slate-700"><X size={12} /></button>
          </span>
        ))}
        <input value={text} autoFocus={autoFocus} aria-label={label} onChange={(e) => setText(e.target.value)} onBlur={() => setTimeout(() => commit(), 150)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown' && sugg.length) { e.preventDefault(); setActive((i) => Math.min(i + 1, sugg.length - 1)); }
            else if (e.key === 'ArrowUp' && sugg.length) { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
            else if ((e.key === 'Enter' || e.key === 'Tab') && sugg.length && text.trim()) { e.preventDefault(); add(sugg[active]); }
            else if (['Enter', ',', ';'].includes(e.key)) { if (commit()) e.preventDefault(); }
            else if (e.key === 'Backspace' && !text && value.length) onChange(value.slice(0, -1));
          }}
          className="flex-1 min-w-[140px] py-1 text-[13.5px] bg-transparent focus:outline-none" />
      </div>
      {sugg.length > 0 && (
        <ul role="listbox" className="absolute z-20 left-0 top-full mt-1 w-full max-w-md bg-white rounded-lg shadow-lg ring-1 ring-slate-200 py-1">
          {sugg.map((s, i) => (
            <li key={s.address} role="option" aria-selected={i === active}>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); add(s); }}
                className={clsx('w-full flex items-center gap-2.5 px-3 py-2 text-left', i === active ? 'bg-sky-50' : 'hover:bg-slate-50')}>
                <span className="w-7 h-7 rounded-full text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0" style={{ background: avatarColor(s.address) }} aria-hidden>{initials(s.name, s.address)}</span>
                <span className="min-w-0"><span className="block text-[13px] text-slate-900 truncate">{s.name || s.address}</span><span className="block text-[12px] text-slate-500 truncate">{s.address}</span></span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
